import { NavMenuItem, useOpenHelpCenter } from '@liscio/common';
import { Icon, List } from '@liscio/ui';

import { AddNewNavMenuItem } from './AddNewNavMenuItem';
import { BulkActionsNavMenuItem } from './BulkActionsNavMenuItem';
import {
  CpaSelectorMenuItem,
  LoggedUserMenuItem,
  OptionsMenuItem,
} from './components';
import { NotificationsNavMenuItem } from './NotificationsNavMenuItem';
import { useFirmViewMode } from 'custom-hooks/useFirmViewMode';
import { useMessageCounts } from 'fetch-utils/messages/messages-hooks';
import { useProfileData } from 'fetch-utils/users/user-hooks';
import { routes } from 'routes';

const FIRM_HELP_CENTER_URL = 'https://intercom.help/liscio-inc/en/';

export const PrimaryNavigationMenu = () => {
  const { isMobile } = useFirmViewMode();
  const { hasSMSElement } = useProfileData();
  const { openHelpCenter } = useOpenHelpCenter(FIRM_HELP_CENTER_URL);
  // Use lightweight counts endpoint instead of fetching full messages
  const { data: messageCounts } = useMessageCounts();

  return (
    <>
      {/* Keep this order in sync with the legacy firm sidebar (get_menu_items). */}
      <List>
        {!isMobile && <AddNewNavMenuItem />}
        <NavMenuItem
          data-testid="sidebar__home"
          icon={<Icon icon="home" />}
          label="Home"
          to={routes.dashboard.link()}
        />
        <NavMenuItem
          data-testid="sidebar__inbox"
          icon={<Icon icon="inbox" />}
          badgeNumber={
            typeof messageCounts?.unread_thread_count === 'number'
              ? messageCounts.unread_thread_count
              : 0
          }
          label="Inbox"
          to={routes.messagesInbox.link()}
        />
        <NavMenuItem
          data-testid="sidebar__tasks"
          icon={<Icon icon="tasks" />}
          label="Tasks"
          to={routes.tasksList.link()}
        />
        {!isMobile && (
          <NavMenuItem
            data-testid="sidebar__notifications"
            icon={<Icon icon="emails" />}
            label="Emails"
            to={routes.emailInbox.link()}
          />
        )}
        {!isMobile && (
          <NavMenuItem
            data-testid="sidebar__requests"
            icon={<Icon icon="requests" />}
            label="Requests"
            to={routes.requestsListProtected.link()}
          ></NavMenuItem>
        )}
        {!isMobile && (
          <NavMenuItem
            data-testid="sidebar__tax_automation"
            icon={<Icon icon="taxAutomation" />}
            label="Smart Tax"
            to={`/smart-tax`}
          ></NavMenuItem>
        )}
        {hasSMSElement && (
          <NavMenuItem
            data-testid="sidebar__sms"
            icon={<Icon icon="sms" />}
            label="SMS"
            to={routes.smsList.link()}
          />
        )}
        <NavMenuItem
          data-testid="sidebar__contacts"
          icon={<Icon icon="contacts" />}
          label="Contacts"
          to={routes.contactsList.link()}
        />
        <NavMenuItem
          data-testid="sidebar__accounts"
          icon={<Icon icon="accounts" />}
          label="Accounts"
          to={routes.accountsList.link()}
        />
        <NavMenuItem
          data-testid="sidebar__files"
          icon={<Icon icon="files" />}
          label="Files"
          // `/files` owns every width (it renders the mobile list when narrow).
          to={routes.files.link()}
        />
        {isMobile && (
          <NavMenuItem
            data-testid="sidebar__upload_file"
            icon={<Icon icon="uploadFile" />}
            label="Upload File"
            to={routes.filesNew.link()}
          />
        )}
        {!isMobile && (
          <>
            <NavMenuItem
              data-testid="sidebar__edocsfiles"
              icon={<Icon icon="edoc" />}
              label="eDocs"
              to={routes.eDocsList.link()}
            />
            <BulkActionsNavMenuItem />
          </>
        )}
        {!isMobile && <NotificationsNavMenuItem />}
        <NavMenuItem
          data-testid="sidebar__need_help"
          icon={<Icon icon="help" />}
          onClick={openHelpCenter}
          label="Need Help?"
        />
      </List>
      <List sx={{ mt: '32px' }}>
        <LoggedUserMenuItem />
      </List>
      {!isMobile && (
        <List sx={{ mt: '31px' }}>
          <CpaSelectorMenuItem />
        </List>
      )}
      {!isMobile && (
        <List>
          <OptionsMenuItem />
        </List>
      )}
    </>
  );
};
